import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import confetti from "canvas-confetti";
import { Sparkles, Copy, Check, ChevronRight, Share2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";

/**
 * WelcomeFounder — full-page celebration shown after checkout for founding
 * members. Reads `founder_number` from auth/me (stamped by the Stripe webhook)
 * and celebrates the moment with confetti + a share-my-seat card.
 */
export default function WelcomeFounder() {
  const { user, loading } = useAuth();
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  // Fire confetti once user data loads
  useEffect(() => {
    if (loading || !user) return;
    if (!user.is_founding_member) return;
    const colors = ["#1B3B2B", "#D4B895", "#8B2626", "#F9F6F0"];
    const end = Date.now() + 1400;
    (function frame() {
      confetti({ particleCount: 4, angle: 60, spread: 65, origin: { x: 0, y: 0.7 }, colors });
      confetti({ particleCount: 4, angle: 120, spread: 65, origin: { x: 1, y: 0.7 }, colors });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  }, [loading, user]);

  const shareUrl = useMemo(() => {
    if (!user) return "";
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const ref = user.ref_code ? `?ref=${user.ref_code}` : "";
    return `${origin}/pricing${ref}`;
  }, [user]);

  const shareText = useMemo(() => {
    if (!user) return "";
    const n = user.founder_number;
    const num = n ? `Founder #${n}` : "a founding member";
    return `I just became ${num} at The Farm To You. 500 founding seats total. Grab one before they close:`;
  }, [user]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* noop */ }
  };

  const nativeShare = async () => {
    try {
      await navigator.share?.({
        title: "The Farm To You — Founding member",
        text: shareText,
        url: shareUrl,
      });
    } catch { /* user cancelled */ }
  };

  // Redirect non-members away
  useEffect(() => {
    if (!loading && user && !user.is_founding_member) {
      const t = setTimeout(() => navigate("/account"), 2000);
      return () => clearTimeout(t);
    }
  }, [loading, user, navigate]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[var(--ff-text-muted)]">Loading…</div>;
  if (!user) return <div className="min-h-screen flex items-center justify-center text-[var(--ff-text-muted)]">Please sign in.</div>;

  const founderNum = user.founder_number;
  const firstName = (user.name || "").split(" ")[0] || "Founder";

  return (
    <div className="min-h-screen bg-[var(--ff-cream)]" data-testid="welcome-founder-page">
      <Seo
        title={`Welcome, Founder${founderNum ? ` #${founderNum}` : ""} · The Farm To You`}
        description="You just claimed a founding seat. Share your link and earn lifetime credits."
        path="/welcome"
      />

      <section className="max-w-4xl mx-auto px-6 pt-20 pb-16 text-center" data-testid="welcome-hero">
        <p className="overline text-[var(--ff-primary)] inline-flex items-center gap-1.5 justify-center">
          <Sparkles size={12}/> Welcome to the founding class
        </p>
        <h1 className="font-display text-5xl sm:text-7xl mt-5 text-[var(--ff-primary)] leading-[1.02]" data-testid="welcome-heading">
          Welcome, {firstName}.
        </h1>
        {founderNum ? (
          <div className="mt-8 inline-flex items-center gap-3 bg-[var(--ff-primary)] text-white rounded-full px-6 py-3" data-testid="welcome-founder-number">
            <span className="text-sm uppercase tracking-wider text-[var(--ff-accent-kraft)]">Founder</span>
            <span className="font-display text-3xl">#{founderNum}</span>
            <span className="text-sm text-white/80">of 500</span>
          </div>
        ) : null}
        <p className="mt-8 text-lg text-[var(--ff-text-secondary)] max-w-xl mx-auto leading-relaxed">
          Your founding seat is locked in for life — founding badge, free shipping, early access to every drop, and lifetime access to member pricing.
        </p>
      </section>

      {/* Share my seat card */}
      <section className="max-w-3xl mx-auto px-6 pb-16" data-testid="welcome-share-card">
        <div className="bg-white rounded-2xl border border-[var(--ff-border)] p-8 sm:p-10 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-5 flex justify-center">
            <div className="bg-white border border-[var(--ff-border)] rounded-lg p-3">
              <QRCodeSVG
                value={shareUrl}
                size={168}
                bgColor="#FFFFFF"
                fgColor="#1B3B2B"
                level="M"
                includeMargin={false}
                data-testid="welcome-share-qr"
              />
            </div>
          </div>
          <div className="md:col-span-7">
            <p className="overline text-[var(--ff-primary)]">Share your seat</p>
            <h2 className="font-display text-3xl mt-2 text-[var(--ff-primary)] leading-tight">
              Invite a friend, earn lifetime credit.
            </h2>
            <p className="mt-3 text-sm text-[var(--ff-text-secondary)] leading-relaxed">
              Every founding friend who signs up through your link earns you 10% of their spend — for the life of their membership.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={copyLink}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="welcome-copy-link"
              >
                {copied ? <Check size={15}/> : <Copy size={15}/>}
                {copied ? "Copied" : "Copy invite link"}
              </button>
              <button
                type="button"
                onClick={nativeShare}
                className="btn-secondary inline-flex items-center gap-2"
                data-testid="welcome-native-share"
              >
                <Share2 size={15}/> Share to…
              </button>
            </div>
            <p className="mt-4 text-xs text-[var(--ff-text-muted)] font-mono break-all" data-testid="welcome-share-url">
              {shareUrl}
            </p>
          </div>
        </div>
      </section>

      {/* Next steps */}
      <section className="max-w-3xl mx-auto px-6 pb-24 text-center">
        <Link
          to="/account"
          className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--ff-primary)] hover:underline"
          data-testid="welcome-account-cta"
        >
          Head to your account <ChevronRight size={14}/>
        </Link>
      </section>
    </div>
  );
}
